import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '../../config/AppConfigContext';

const joinUrl = (baseUrl: string, path: string) => {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

interface SendMessageParams {
  userId: string;
  companyId: string;
  message: string;
  conversationId?: string | null;
}

interface SendMessageResponse {
  conversationId: string;
  response: string;
}

export const useSendMessageMutation = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SendMessageParams): Promise<SendMessageResponse> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');

      const token = auth0Audience
        ? await getAccessTokenSilently({ authorizationParams: { audience: auth0Audience } })
        : await getAccessTokenSilently();

      const body: Record<string, string> = {
        userId: params.userId,
        companyId: params.companyId,
        message: params.message,
      };
      if (params.conversationId) body.conversationId = params.conversationId;

      const response = await fetch(joinUrl(allyApi, 'api/v1/partner/chat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || `HTTP ${response.status}`);
      }

      return response.json() as Promise<SendMessageResponse>;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['partner', 'users', variables.userId, 'conversations'],
      });
    },
  });
};
